import { AlertCircle, BookOpen, FileText } from "lucide-react";
import type { ReactNode } from "react";

import { SectionHeader } from "components/SectionHeader";
import { EmptyState, LoadingCards } from "components/states";
import { SECTION_TITLES } from "../../copy/section-titles";
import { Card } from "components/ui/card";

import {
  ResourceFailureScope,
  ResourceOpenControl,
  useResourceDownload,
} from "./ResourceOpenLink";
import { resourceTypeLabel } from "./resource-type";
import type { Resource } from "./schemas";
import { useVisibleResources } from "./use-event-content";

/**
 * `seeAll` picks the one-query listing that only admins are allowed. The
 * session's platform role is a hint for that choice, not the authority:
 * firestore.rules decides, and a stale role shows as the load error below.
 */
export function ResourcesSection({
  eventId,
  uid,
  seeAll,
}: {
  eventId: string;
  uid: string;
  seeAll: boolean;
}) {
  const resources = useVisibleResources(eventId, uid, seeAll);

  const count = resources.status === "success" ? resources.data.length : 0;
  const head = (
    <SectionHeader
      className="mb-6"
      kicker={SECTION_TITLES.resources.kicker}
      title={SECTION_TITLES.resources.title}
      subline={
        count > 0
          ? `${count} ${count === 1 ? "file" : "files"} to open.`
          : undefined
      }
    />
  );

  if (resources.status === "loading")
    return (
      <Frame>
        {head}
        <LoadingCards cards={3} />
      </Frame>
    );
  if (resources.status === "error")
    return (
      <Frame>
        {head}
        <EmptyState icon={AlertCircle} title="Could not load resources">
          {resources.error}
        </EmptyState>
      </Frame>
    );
  if (resources.data.length === 0)
    return (
      <Frame>
        {head}
        <EmptyState icon={FileText} title="No resources yet">
          Resources will appear here once uploaded.
        </EmptyState>
      </Frame>
    );

  return (
    <Frame>
      {head}
      <ResourceFailureScope testId="resources-failed">
        {/* Each row is a direct child: the end-to-end specs find a row by it. */}
        <div className="space-y-3" data-testid="resources-content">
          {resources.data.map((r) => (
            <ResourceRow key={r.id} resource={r} />
          ))}
        </div>
      </ResourceFailureScope>
    </Frame>
  );
}

/** The tab's column, capped on a desktop so a row does not stretch thin. */
function Frame({ children }: { children: ReactNode }) {
  return <div className="mx-auto w-full max-w-3xl">{children}</div>;
}

function ResourceRow({ resource }: { resource: Resource }) {
  const download = useResourceDownload(resource.storagePath);
  const meta = [
    resourceTypeLabel(resource.contentType),
    resource.size != null ? formatSize(resource.size) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card className="rounded-[22px] border-border bg-card p-3.5">
      {/* Wraps: at 320px the name, the meta line and the 44px Open control do
          not share one line, so the control drops under them, right-aligned. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex min-w-0 flex-[1_1_14rem] items-center gap-3">
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-hb-blue-fill text-white"
          >
            <BookOpen className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-[1.1875rem] leading-[1.15] text-foreground wrap-anywhere">
              {resource.name}
            </p>
            {resource.description && (
              <p className="text-sm text-muted-foreground wrap-anywhere">
                {resource.description}
              </p>
            )}
            {meta && (
              <p className="pt-0.5 text-xs font-semibold text-muted-foreground">
                {meta}
              </p>
            )}
            {download.status === "error" && (
              <p className="text-sm text-destructive-text">
                Could not load that file.
              </p>
            )}
          </div>
        </div>
        <div className="ml-auto">
          <ResourceOpenControl
            download={download}
            name={resource.name}
            testId={`open-resource-${resource.id}`}
          />
        </div>
      </div>
    </Card>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
